import {
  theme,
  Card,
  Empty,
  Flex,
  Space,
  Spin,
  Tooltip,
  Typography,
} from 'antd'
import { ReactNode } from 'react'

const { Text } = Typography

interface RepartitionBarItem {
  label: string
  value: number
  color?: string
  /**
   * Share (0-100) already rounded by the API. When EVERY item carries one, the
   * bar and legend show these figures as-is instead of computing shares from
   * `value` (membership cards). Absent: legacy client-side computation.
   */
  percent?: number
}

interface Props {
  data: RepartitionBarItem[]
  isLoading?: boolean
  height?: number
  /**
   * Opt-in, API-percent mode only: the legend shows just the N slices with the
   * highest `percent` (the bar still draws every segment), then a muted
   * "+ n others" whose tooltip lists the rest.
   */
  maxLegendItems?: number
}

const Dot = ({ color }: { color: string }) => (
  <span
    style={{
      display: 'inline-block',
      width: 8,
      height: 8,
      borderRadius: '50%',
      backgroundColor: color,
    }}
  />
)

/**
 * Whole percentages that always add up to 100, by largest remainder.
 *
 * Rounding each share on its own drifts: three equal thirds each round to 33
 * and the legend reads 99%, while 2/3 + 1/6 + 1/6 rounds to 67 + 17 + 17 and
 * reads 101%. Here every share takes its floor first, then the points left over
 * go to the largest fractional parts, biggest first — so the legend sums to 100
 * and each label stays within one point of its exact share.
 */
const wholeShares = (values: number[], total: number): number[] => {
  if (total <= 0) {
    return values.map(() => 0)
  }

  const exact = values.map((value) => (value / total) * 100)
  const shares = exact.map(Math.floor)

  let leftover = 100 - shares.reduce((sum, share) => sum + share, 0)

  const byLargestFraction = exact
    .map((value, index) => ({ index, fraction: value - Math.floor(value) }))
    // Equal remainders are settled by the smaller slice, so two slices holding
    // the same count always display the same percentage and the drift lands on
    // the dominant one, where a single point is least visible.
    .sort(
      (a, b) => b.fraction - a.fraction || values[a.index] - values[b.index]
    )

  for (const { index, fraction } of byLargestFraction) {
    // A zero fraction is already exact: never inflate it to soak up a point.
    if (leftover <= 0 || fraction === 0) break
    shares[index] += 1
    leftover -= 1
  }

  return shares
}

/**
 * Horizontal stacked bar + inline legend (dot, label, share), generic over any
 * `{ label, value, color }[]`. Matches Figma 800-2320 / 799-1192.
 */
const RepartitionBar = ({
  data,
  isLoading,
  height = 40,
  maxLegendItems,
}: Props) => {
  const { token } = theme.useToken()

  const palette = [
    // The three designed slice colours.
    token.colorSuccess,
    token.colorWarning,
    '#8c8c8c',
    // Brand tones for the slices the design does not cover.
    token.colorPrimary,
    '#b5a9b8',
    '#9a8474',
  ]

  const total = data.reduce((acc, item) => acc + item.value, 0)
  const hasApiPercent =
    data.length > 0 && data.every((item) => typeof item.percent === 'number')
  // Displayed percentages. The bar keeps the exact fractions for its widths,
  // which already add up to 100 without rounding. API percentages win.
  const shares = hasApiPercent
    ? data.map((item) => item.percent as number)
    : wholeShares(
        data.map((item) => item.value),
        total
      )

  if (isLoading) {
    return (
      <Flex align="center" justify="center" style={{ minHeight: 80 }}>
        <Spin />
      </Flex>
    )
  }

  if (data.length === 0 || total === 0) {
    return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
  }

  // Legend entries, each keeping its original index (palette fallback colour
  // and share). Limited and sorted by the API's percent only on request.
  const entries = data.map((item, index) => ({ item, index }))
  const limited =
    hasApiPercent &&
    typeof maxLegendItems === 'number' &&
    entries.length > maxLegendItems
  const ranked = limited
    ? [...entries].sort(
        (a, b) => (b.item.percent as number) - (a.item.percent as number)
      )
    : entries
  const legendEntries = limited ? ranked.slice(0, maxLegendItems) : entries
  const hiddenEntries = limited ? ranked.slice(maxLegendItems) : []

  return (
    <Space orientation="vertical" className="w-full" size={24}>
      <div
        style={{
          display: 'flex',
          width: '100%',
          height,
          borderRadius: token.borderRadiusSM,
          overflow: 'hidden',
        }}
      >
        {data.map((item, index) => {
          const percent = hasApiPercent
            ? (item.percent as number)
            : (item.value / total) * 100
          if (percent <= 0) return null

          const segment = (
            <div
              key={item.label}
              // Native hint for the legacy callers; the API-percent mode has
              // its own tooltip below.
              title={
                hasApiPercent
                  ? undefined
                  : `${item.label}: ${percent.toFixed(1)}%`
              }
              style={{
                width: `${percent}%`,
                backgroundColor: item.color ?? palette[index % palette.length],
              }}
            />
          )

          return hasApiPercent ? (
            <Tooltip
              key={item.label}
              title={`${item.label} · ${item.percent}% (${item.value})`}
            >
              {segment}
            </Tooltip>
          ) : (
            segment
          )
        })}
      </div>
      <Flex wrap gap={32}>
        {legendEntries.map(({ item, index }) => (
          <Flex key={item.label} vertical gap={4}>
            <Flex align="center" gap={8}>
              <Dot color={item.color ?? palette[index % palette.length]} />
              <Text style={{ fontSize: 14, color: token.colorTextTertiary }}>
                {item.label}
              </Text>
            </Flex>
            <Text
              style={{
                fontSize: 20,
                fontWeight: 700,
                lineHeight: 1.2,
                color: token.colorText,
              }}
            >
              {shares[index]}%
            </Text>
          </Flex>
        ))}
        {hiddenEntries.length > 0 && (
          <Tooltip
            title={
              <div style={{ maxHeight: 240, overflowY: 'auto' }}>
                {hiddenEntries.map(({ item }) => (
                  <div key={item.label}>
                    {item.label} · {item.percent}%
                  </div>
                ))}
              </div>
            }
          >
            <Text
              tabIndex={0}
              style={{
                alignSelf: 'center',
                fontSize: 14,
                color: token.colorTextTertiary,
                cursor: 'default',
              }}
            >
              + {hiddenEntries.length} others
            </Text>
          </Tooltip>
        )}
      </Flex>
    </Space>
  )
}

interface RepartitionCardProps {
  title: ReactNode
  data: RepartitionBarItem[]
  isLoading?: boolean
  error?: ReactNode
  maxLegendItems?: number
}

/** The card the repartition bar always ships in: 14px tertiary label, 24px padding. */
const RepartitionCard = ({
  title,
  data,
  isLoading,
  error,
  maxLegendItems,
}: RepartitionCardProps) => {
  const { token } = theme.useToken()

  return (
    <Card
      style={{ height: '100%', borderRadius: 8 }}
      styles={{ body: { padding: 24 } }}
    >
      <Flex vertical gap={20}>
        <Text style={{ fontSize: 14, color: token.colorTextTertiary }}>
          {title}
        </Text>
        <RepartitionBar
          data={data}
          isLoading={isLoading}
          maxLegendItems={maxLegendItems}
        />
        {error && <Text type="danger">{error}</Text>}
      </Flex>
    </Card>
  )
}

export { RepartitionBar, RepartitionCard, type RepartitionBarItem }
